import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BFF_ERROR_CODES } from '../constants/bff-error-messages';
import { LoggerService } from './logger.service';
import { LogEvent } from '../enums/log-event';

const REFRESH_INTERVAL_MS = 1_800_000;
// const REFRESH_INTERVAL_MS = 5_000;

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshSchedulerService {
  private readonly refreshUrl = `${environment.externalUrls.bff}/api/auth/refreshToken`;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  private readonly sessionExpiredSub = new BehaviorSubject<boolean>(false);
  public readonly sessionExpired$ = this.sessionExpiredSub.asObservable();

  constructor(
    private httpClient: HttpClient,
    private loggerService: LoggerService,
    private ngZone: NgZone
  ) {}

  start(): void {
    if (this.intervalId !== null) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => {
        this.refresh();
      }, REFRESH_INTERVAL_MS);
    });
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  expireSession(): void {
    this.stop();
    this.ngZone.run(() => {
      this.sessionExpiredSub.next(true);
    });
  }

  private refresh(): void {
    this.httpClient
      .post(this.refreshUrl, null, { withCredentials: true })
      .subscribe({
        next: () => {
          this.loggerService.info(
            LogEvent.LIVECHAT_JWT_SCHEDULED_REFRESH_SUCCESS
          );
        },
        error: (err: HttpErrorResponse) => {
          if (
            err.error?.code === BFF_ERROR_CODES.INVALID_JWT_REFRESH_TOKEN
          ) {
            this.stop();
            this.loggerService.warn(
              LogEvent.LIVECHAT_JWT_SCHEDULED_REFRESH_EXPIRED
            );
            this.ngZone.run(() => {
              this.sessionExpiredSub.next(true);
            });
          }

          if (
            err.error?.code === BFF_ERROR_CODES.INVALID_REFRESH_TOKEN_COOKIE_NOT_FOUND
          ) {
            this.stop();
            this.loggerService.warn(
              LogEvent.LIVECHAT_JWT_SCHEDULED_REFRESH_COOKIE_NOT_FOUND
            );
            this.ngZone.run(() => {
              this.sessionExpiredSub.next(true);
            });
          }

          if (err.error?.code === BFF_ERROR_CODES.SESSION_EXPIRED) {
            this.loggerService.warn(LogEvent.LIVECHAT_SESSION_EXPIRED);
            this.expireSession();
          }
        },
      });
  }
}
